import { Link } from 'react-router-dom'

const guide = {
  slug: 'how-to-block-porn-on-ipad',
  category: 'device',
  kicker: 'iPad',
  title: 'How to block porn on iPad — and why the iPad is the device people forget',
  seoTitle: 'How to Block Porn on iPad in 2026 (So It Stays Blocked)',
  description: `Screen Time, a DNS profile or a supervised iPad: how to block porn on iPadOS, why the tablet is usually the door left open, and the setup that holds.`,
  published: '2026-09-03',
  minutes: 7,
  intro: `Most people lock the phone and forget the iPad. It lives on the
          couch or by the bed, it has the biggest screen in the house, and it
          runs the same Safari as the phone. This guide covers the three ways
          to block porn on an iPad, from the free one to the one that cannot be
          undone, and how to treat the iPad and the iPhone as one problem.`,
  next: ['how-to-block-porn-on-iphone', 'is-screen-time-enough-to-block-porn', 'porn-blocker-you-cant-uninstall'],
  body: (
    <>
      <h2>Why the iPad is the device that gets forgotten</h2>
      <p>
        When people decide to quit, they lock the phone, because the phone is
        the device in their pocket. The iPad stays as it was. It runs the same
        apps, the same Safari, and it is usually the one within reach at the
        hour a relapse happens. A block that covers one device and not the
        other isn't a block; it's a detour. The rule is simple: <b>every
        device you own, or none of them.</b>
      </p>

      <h2>Method 1 — Screen Time on iPad (free, two minutes)</h2>
      <p>iPadOS ships the same content filter as the iPhone:</p>
      <ol>
        <li>Open <b>Settings → Screen Time</b> and turn it on.</li>
        <li>Tap <b>Content &amp; Privacy Restrictions</b> and enable them.</li>
        <li>Go to <b>Content Restrictions → Web Content</b> and pick <b>Limit Adult Websites</b>.</li>
        <li>Set a <b>Screen Time passcode</b>, separate from the unlock code.</li>
      </ol>
      <p>
        Worth doing today. It filters adult sites in Safari and removes
        Private Browsing while it's on. If the iPad and the iPhone share the
        same Apple ID, you can turn on <b>Share Across Devices</b> in Screen
        Time so the settings follow you.
      </p>
      <p>
        Where it stops: you know the passcode, and even when someone else set
        it, Apple lets it be reset with the Apple ID signed in on the device,
        which is yours. It's a website filter, so it doesn't look inside apps.
        And on an iPad it has an extra weakness: Split View and Slide Over
        make a second browser one swipe away. Screen Time is a speed bump.
        The full list of workarounds is in
        <Link to="/guides/is-screen-time-enough-to-block-porn"> is Screen Time enough</Link>.
      </p>

      <h2>Method 2 — A filtering DNS profile</h2>
      <p>
        A DNS filter works underneath the browser. Every app on the iPad has
        to ask "where is this domain?" before it can load anything; a
        filtering resolver refuses whole categories, so pornography never
        resolves, in Safari, in Chrome, in a private tab, or inside an app.
        On iPad you install it as a configuration profile that sets an
        encrypted DNS resolver, or through an app that does the same thing.
      </p>
      <p>
        Where it stops: <b>Settings → General → VPN &amp; Device Management →
        Remove Profile</b>. Four taps, no password. A profile you installed
        is a profile you can remove. The filter is the right tool; the way
        it's attached to the iPad is not.
      </p>

      <h2>Method 3 — A supervised iPad (the one that stays)</h2>
      <p>
        Supervision is Apple's mechanism for organisations that hand out
        iPads and need settings to stick. On a supervised iPad, a
        configuration profile can be marked non-removable: the Remove Profile
        button isn't there. The same profile can forbid adding a VPN, which
        closes the usual back door.
      </p>
      <p>
        That is how SELFCNTRL works on iPad, exactly as on iPhone. The filter
        is the DNS category filter from method 2; supervision is what keeps it
        on. Taking it off is not a button at 2am: it's a deliberate request
        from your account, with a cooling-off delay. You stay free to stop.
        You can't stop on an impulse.
      </p>
      <p>Setup, once, about ten minutes:</p>
      <ol>
        <li><Link to="/register">Create your space</Link>. One email address.</li>
        <li>Plug the iPad into a Mac or a Windows PC with a cable and run the SELFCNTRL supervisor. Back it up first: putting a device in supervised mode can involve a reset, and the supervisor walks you through restoring it.</li>
        <li>From your account, scan a QR code with the iPad's camera. The profile installs and seals itself.</li>
        <li>Pick your pace and, if you want, a night curfew that cuts the internet entirely between, say, 11pm and 7am.</li>
      </ol>
      <p>
        Do the iPhone the same way, from the same account. Both devices show
        up in your space; the filter, the pace and the curfew apply to all of
        them. The iPhone steps are in
        <Link to="/guides/how-to-block-porn-on-iphone"> the iPhone guide</Link>.
      </p>

      <h2>iPad-specific questions</h2>
      <h3>Does supervision change how the iPad works day to day?</h3>
      <p>
        No. Supervision here does one thing: keep the filter from being taken
        off. Nobody reads your messages, no one can wipe the device or see
        where it is, and there is no report, because category filtering keeps
        no history to report. Apps, games, drawing, video, everything else is
        untouched.
      </p>
      <h3>What about a shared family iPad?</h3>
      <p>
        The filter is per device, not per person, so everyone using the iPad
        gets the same block. For a device shared with children that's usually
        what you want. If another adult needs unfiltered access, they need
        their own device.
      </p>
      <h3>Does an old iPad work?</h3>
      <p>
        Supervision is a feature of iPadOS, not of a specific model. If the
        iPad runs a current iPadOS and can be plugged into a computer, it can
        be supervised.
      </p>

      <h2>The three methods, side by side</h2>
      <table>
        <thead>
          <tr><th>Question</th><th>Screen Time</th><th>DNS profile</th><th>Supervised</th></tr>
        </thead>
        <tbody>
          <tr><td>Covers every app, not just Safari</td><td>No</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Covers private browsing</td><td>Yes (disables it)</td><td>Yes</td><td>Yes</td></tr>
          <tr><td>Removable in under a minute</td><td>Yes (passcode or Apple ID reset)</td><td>Yes (Remove Profile)</td><td>No</td></tr>
          <tr><td>Blocks adding a workaround VPN</td><td>No</td><td>No</td><td>Yes</td></tr>
          <tr><td>Same lock on the iPhone too</td><td>If shared across devices</td><td>Install it twice</td><td>Same account, same QR code</td></tr>
        </tbody>
      </table>

      <h2>The bet</h2>
      <p>
        On a device we locked, if you strip the filter off by your own means,
        we send you $100 on top of a full refund. The iPad counts. Details in
        the <Link to="/remboursement">refund policy</Link>.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Can I block porn on iPad without a computer?',
      a: `Screen Time and a DNS profile need no computer. The supervised
          lock, the one that doesn't come off, needs a Mac or a Windows PC
          once, for about ten minutes. After that the computer is never
          needed again.`
    },
    {
      q: 'Will supervising the iPad erase it?',
      a: `It can involve a reset, depending on the device's state. Back it up
          first (iCloud or a computer) and restore afterwards: apps, photos
          and documents come back. The supervisor walks you through it.`
    },
    {
      q: 'Does the block cover apps like YouTube, Reddit or Telegram?',
      a: `The category filter works at DNS level, so it applies to every app,
          not only browsers. Content inside an app that isn't classified as
          adult, like a mixed platform, is a different question: SELFCNTRL
          lets you block additional categories or specific domains from your
          account, and tightening takes effect immediately.`
    },
    {
      q: 'I only have an iPad, no iPhone. Does it still make sense?',
      a: `Yes. One device is locked the same way as five. The point is that
          every device you actually use is covered.`
    }
  ]
}

export default guide
